type NativeLog = { timestamp: number; code: string; level: string; message: string }
const KNOWN_CODES = new Set([
  'None',
  'NoUpdatesAvailable',
  'UpdateAssetsNotAvailable',
  'UpdateServerUnreachable',
  'UpdateHasInvalidSignature',
  'UpdateCodeSigningError',
  'UpdateFailedToLoad',
  'AssetsFailedToLoad',
  'JSRuntimeError',
  'InitializationError',
  'Unknown',
])
const KNOWN_LEVELS = new Set(['trace', 'debug', 'info', 'warn', 'error', 'fatal'])

/** Native messages may contain signed URLs or request headers. Only emit
 * allowlisted classifications, never raw messages, asset IDs or stack traces. */
export function summarizeOtaLogs(entries: NativeLog[], since: number, now = Date.now()) {
  return entries
    .filter((entry) => entry.timestamp >= since && entry.timestamp <= now)
    .slice(-20)
    .map((entry) => ({
      code: KNOWN_CODES.has(entry.code) ? entry.code : 'Unknown',
      level: KNOWN_LEVELS.has(entry.level) ? entry.level : 'unknown',
      age_ms: now - entry.timestamp,
      storage_full: /ENOSPC|no space left|disk (?:is )?full/i.test(entry.message),
      timeout: /timed? ?out|timeout/i.test(entry.message),
      unreachable: /Unable to resolve host|UnknownHostException|network is unreachable/i.test(
        entry.message,
      ),
      // Android: `failed to connect to <host> … after 10000ms` o ConnectException.
      // No prueba que falte red (puede ser firewall, portal cautivo o el propio
      // servidor), así que no degrada nada: solo deja la causa legible.
      connect_failed: /failed to connect to|ConnectException|Connection refused|ECONNREFUSED/i.test(
        entry.message,
      ),
      tls_failed:
        /SSLHandshakeException|SSLException|CertPathValidatorException|Trust anchor for certification path/i.test(
          entry.message,
        ),
      // EAS respondió, pero con un status que no es 2xx.
      http_not_successful: /Remote update request not successful/i.test(entry.message),
      http_access_denied: /(?:HTTP|status(?: code)?|response code)\s*[:=]?\s*(401|403)\b/i.test(
        entry.message,
      ),
      asset_missing: /(?:HTTP|status(?: code)?|response code)\s*[:=]?\s*404\b/i.test(entry.message),
      // Un protocolo roto (manifiesto/JSON inválido o multipart corrupto) sí es
      // accionable: una update publicada nunca podría aplicarse. Los cortes de
      // red, en cambio, se reintentan solos.
      manifest_invalid:
        /Invalid update response|Failed to parse manifest|manifest JSON|Failed to parse multipart/i.test(
          entry.message,
        ),
    }))
}

export type OtaLogSummary = ReturnType<typeof summarizeOtaLogs>[number]
export type OtaDiagnostics = { native_log_status: string; native_logs?: OtaLogSummary[] }

/**
 * Códigos que prueban que una update PUBLICADA no se puede aplicar: firma,
 * code signing, inicialización rota, runtime JS roto. Estos conservan su
 * evidencia en Sentry; el resto de fallos de una operación OTA no.
 */
const ACTIONABLE_FAILURE_CODES = new Set([
  'UpdateHasInvalidSignature',
  'UpdateCodeSigningError',
  'InitializationError',
  'JSRuntimeError',
])

function isExpoUpdatesOperationRejection(error: unknown) {
  const { code, message } = (error ?? {}) as { code?: unknown; message?: unknown }
  if (code === 'ERR_UPDATES_CHECK' || code === 'ERR_UPDATES_FETCH') return true
  return (
    typeof message === 'string' &&
    /ERR_UPDATES_(?:CHECK|FETCH)|ExpoUpdates\.(?:checkForUpdateAsync|fetchUpdateAsync)/i.test(
      message,
    )
  )
}

const ACTIONABLE_MESSAGE =
  /UpdateHasInvalidSignature|UpdateCodeSigningError|InitializationError|JSRuntimeError|invalid signature|code signing|manifest/i

/**
 * TAGO-MOBILE-5C / 5N / 4M — cuándo una operación OTA fallida deja de ser una
 * alerta de producción.
 *
 * Un update de EAS es best-effort: el bundle actual sigue funcionando y el hook
 * reintenta solo (probes de hotfix, foreground y próximo arranque). Ningún
 * fallo de transporte nuestro —red caída, timeout, un fetch cortado, el
 * `undefined reason` que el puente iOS pierde— se puede arreglar desde acá, y
 * reportarlos convirtió el canal en la misma alerta que volvía siempre: 5C y 5N
 * en iOS, 4M en Android, incluso después de resolverse a mano.
 *
 * Lo ÚNICO accionable es lo que impide APLICAR una update publicada: firma
 * inválida, code signing, manifiesto/protocolo roto, inicialización caída, TLS,
 * rechazos HTTP del servidor de assets o disco lleno. Eso sigue llegando a
 * Sentry con su código/flag nativo; todo lo demás queda como breadcrumb con los
 * diagnósticos adjuntos.
 */
export function isExpectedOtaOperationFailure(
  error: unknown,
  diagnostics: OtaDiagnostics,
): boolean {
  if (!isExpoUpdatesOperationRejection(error)) return false
  const { message } = (error ?? {}) as { message?: unknown }
  if (typeof message === 'string' && ACTIONABLE_MESSAGE.test(message)) return false
  const logs = diagnostics.native_logs ?? []
  if (logs.some((entry) => ACTIONABLE_FAILURE_CODES.has(entry.code))) return false
  if (
    logs.some(
      (entry) =>
        entry.storage_full ||
        entry.tls_failed ||
        entry.http_not_successful ||
        entry.http_access_denied ||
        entry.asset_missing ||
        entry.manifest_invalid,
    )
  ) {
    return false
  }
  return true
}

export async function readOtaDiagnostics(
  readLogs: (maxAge: number) => Promise<NativeLog[]>,
  since: number,
  timeoutMs = 800,
): Promise<OtaDiagnostics> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve()
        .then(() => readLogs(Math.max(60_000, Date.now() - since)))
        .then((logs) => ({
          native_log_status: 'available',
          native_logs: summarizeOtaLogs(logs, since),
        })),
      new Promise<OtaDiagnostics>((resolve) => {
        timer = setTimeout(() => resolve({ native_log_status: 'timeout' }), timeoutMs)
      }),
    ])
  } catch {
    // Diagnostics failure cannot replace or prevent reporting the original error.
    return { native_log_status: 'unavailable' }
  } finally {
    if (timer) clearTimeout(timer)
  }
}
